import { _notNil, _isEmptyString } from '@/littledash';
import { cellText } from '@/support/HotCellValue';
import type { CellValue } from '@/support/hot.types';

import { glossaryLabelMaxLengthMessage, MAX_GLOSSARY_LABEL_LENGTH } from '@/constants/glossary';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';

/** Row tuple for observation type spreadsheets: [title, label, scoringScale, gradingInfo] */
export type ObservationTypeRow = [string, string, string, string];

/**
 * Reads Handsontable grid rows as observation type rows, so validation compares text whatever the cells hold.
 */
export const toObservationTypeRows = (data: Array<Array<CellValue>>): Array<ObservationTypeRow> =>
  data.map((row) => [cellText(row[0]) ?? '', cellText(row[1]) ?? '', cellText(row[2]) ?? '', cellText(row[3]) ?? '']);

type ObservationTypeBase = Pick<TeamGlossaryV1, 'api_id' | 'title' | 'label'>;

export const DEFAULT_NUMERIC_SCALE_MIN = 0;
export const DEFAULT_NUMERIC_SCALE_MAX = 1000;

export interface ValidationData {
  existing_titles: Array<string>;
  existing_labels: Array<string>;
  in_use_team_glossary_api_ids: Array<string>;
}

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export type ScoringScale =
  | { type: 'numeric'; minValue: number; maxValue: number }
  | { type: 'severity'; severityLevels?: Array<{ level: number; label: string; description?: string }> };

export function validateObservationTypeTitle(
  value: string,
  rowIndex: number,
  validationData: ValidationData | null,
  currentObservationTypes: Array<ObservationTypeBase> = [],
  spreadsheetData: Array<ObservationTypeRow> = []
): ValidationResult {
  if (!_notNil(validationData)) {
    return { isValid: true, message: 'Loading validation data...' };
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return { isValid: false, message: 'Observation type is required' };
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return { isValid: false, message: 'Observation type is required' };
  }

  if (trimmedValue.length > 255) {
    return { isValid: false, message: 'Observation type must be 255 characters or less' };
  }

  const currentObservationType = currentObservationTypes[rowIndex];
  if (_notNil(currentObservationType)) {
    const isInUse = validationData.in_use_team_glossary_api_ids.includes(currentObservationType.api_id);
    if (isInUse && trimmedValue !== currentObservationType.title) {
      return { isValid: false, message: 'Cannot change title while observations reference this type' };
    }
  }

  const currentTitles = currentObservationTypes.map((ot) => ot.title);
  const existingTitles = validationData.existing_titles.filter((title) => !currentTitles.includes(title));
  const lowerTrimmedValue = trimmedValue.toLowerCase();

  if (spreadsheetData.length > 0) {
    const hasDuplicateInSpreadsheet = spreadsheetData.some((row, index) => {
      if (index === rowIndex) {
        return false;
      }
      const title = row[0]?.trim() ?? '';
      return !_isEmptyString(title) && title.toLowerCase() === lowerTrimmedValue;
    });

    if (hasDuplicateInSpreadsheet) {
      return { isValid: false, message: 'Observation type must be unique within the spreadsheet' };
    }
  }

  const hasDuplicateInExisting = existingTitles.some((title) => title.toLowerCase() === lowerTrimmedValue);

  if (hasDuplicateInExisting) {
    return { isValid: false, message: 'Observation type must be unique' };
  }

  return { isValid: true };
}

export function validateObservationTypeLabel(
  value: string,
  rowIndex: number,
  validationData: ValidationData | null,
  currentObservationTypes: Array<ObservationTypeBase> = [],
  spreadsheetData: Array<ObservationTypeRow> = []
): ValidationResult {
  if (!_notNil(validationData)) {
    return { isValid: true, message: 'Loading validation data...' };
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return { isValid: true, message: 'Label is optional' };
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return { isValid: true, message: 'Label is optional' };
  }

  if (trimmedValue.length > MAX_GLOSSARY_LABEL_LENGTH) {
    return { isValid: false, message: glossaryLabelMaxLengthMessage };
  }

  const currentLabels = currentObservationTypes.map((ot) => ot.label ?? '').filter((label) => label !== '');
  const existingLabels = validationData.existing_labels.filter((label) => !currentLabels.includes(label));
  const lowerTrimmedValue = trimmedValue.toLowerCase();

  if (spreadsheetData.length > 0) {
    const hasDuplicateInSpreadsheet = spreadsheetData.some((row, index) => {
      if (index === rowIndex) {
        return false;
      }
      const label = row[1]?.trim() ?? '';
      return !_isEmptyString(label) && label.toLowerCase() === lowerTrimmedValue;
    });

    if (hasDuplicateInSpreadsheet) {
      return { isValid: false, message: 'Label must be unique within the spreadsheet' };
    }
  }

  const hasDuplicateInExisting = existingLabels.some((label) => label.toLowerCase() === lowerTrimmedValue);

  if (hasDuplicateInExisting) {
    return { isValid: false, message: 'Label must be unique' };
  }

  return { isValid: true };
}

export function validateScoringScale(scale: ScoringScale | null): ValidationResult {
  if (!_notNil(scale)) {
    return { isValid: true };
  }

  if (scale.type === 'numeric') {
    const min = Number(scale.minValue);
    const max = Number(scale.maxValue);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return { isValid: false, message: 'Numeric scale requires min and max values' };
    }

    if (min < 0) {
      return { isValid: false, message: 'Min value must be 0 or greater' };
    }

    if (min >= max) {
      return { isValid: false, message: 'Min value must be less than max value' };
    }

    return { isValid: true };
  }

  if (scale.type === 'severity') {
    if (_notNil(scale.severityLevels) && scale.severityLevels.length > 0) {
      const levels = scale.severityLevels.map((sl) => sl.level);
      const uniqueLevels = new Set(levels);
      if (levels.length !== uniqueLevels.size) {
        return { isValid: false, message: 'Severity levels must be unique' };
      }
    }

    return { isValid: true };
  }

  return { isValid: true };
}

export function parseScoringScale(value: string): ScoringScale | null {
  if (!_notNil(value) || _isEmptyString(value)) {
    return null;
  }

  const numericMatch = value.match(/^Numeric \((\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\)$/);
  if (_notNil(numericMatch)) {
    return {
      type: 'numeric',
      minValue: parseFloat(numericMatch[1]),
      maxValue: parseFloat(numericMatch[2]),
    };
  }

  if (value === 'Severity') {
    return {
      type: 'severity',
      severityLevels: [],
    };
  }

  return null;
}

export function formatScoringScale(scale: ScoringScale | null): string {
  if (!_notNil(scale)) {
    return '';
  }

  if (scale.type === 'numeric') {
    return `Numeric (${scale.minValue}-${scale.maxValue})`;
  }

  if (scale.type === 'severity') {
    return 'Severity';
  }

  return '';
}

export function getBasicObservationTypeErrorMessage(value: string, validationData: ValidationData | null): string {
  if (!_notNil(validationData)) {
    return 'Loading validation data...';
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return 'Observation type is required';
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return 'Observation type is required';
  }

  if (trimmedValue.length > 255) {
    return 'Observation type must be 255 characters or less';
  }

  const lowerTrimmedValue = trimmedValue.toLowerCase();
  const lowerExistingTitles = validationData.existing_titles.map((title) => title.toLowerCase());

  if (lowerExistingTitles.includes(lowerTrimmedValue)) {
    return 'Observation type must be unique';
  }

  return 'Observation type is invalid';
}

export function getBasicLabelErrorMessage(value: string, validationData: ValidationData | null): string {
  if (!_notNil(validationData)) {
    return 'Loading validation data...';
  }

  if (!_notNil(value) || typeof value !== 'string') {
    return 'Label is optional';
  }

  const trimmedValue = value.trim();

  if (_isEmptyString(trimmedValue)) {
    return 'Label is optional';
  }

  if (trimmedValue.length > MAX_GLOSSARY_LABEL_LENGTH) {
    return glossaryLabelMaxLengthMessage;
  }

  const lowerTrimmedValue = trimmedValue.toLowerCase();
  const lowerExistingLabels = validationData.existing_labels.map((label) => label.toLowerCase());

  if (lowerExistingLabels.includes(lowerTrimmedValue)) {
    return 'Label must be unique';
  }

  return 'Label is invalid';
}
